import React, { type ReactElement, useEffect, useRef, useState } from 'react';
// import { useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { Logger, useDetectScroll } from '@saviynt/common';
import {
  AlertBanner,
  Box,
  Button,
  ButtonSelect,
  FooterActionBar,
  Icon,
  InputField,
  Link,
  Loader,
  ModalIsland,
  Switch,
  Typography,
} from '@saviynt/design-system';

import { testIds } from '../../../../tests';
import { CF_NEU_BLD, CF_NEU_MED, CF_NEU_STL } from '../../../constants';
import { useInvertedLocalize, useTypedSelector } from '../../../hooks';

import { MOCK_MITIGATE_DATA } from './mockMitigateList';
import SodMitigateListItem from './SodMitigateListItem';

import './SodMitigateModal.css';

interface SelectedDates {
  startDate: Date | null;
  endDate: Date | null;
}

export interface MitigateItemType {
  id: string;
  title: string;
  description: string;
  priority: string;
  mitigateType: string; // Need scema for this
  isSelected: boolean;
  isRecommended: boolean;
  isCritical: boolean;
  selectedDates: SelectedDates;
  expirationDate: string;
  comment: string;
}

interface SodMitigateModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export type DateTypeOptions = 'startDate' | 'endDate';

/**
 * * TODO
 * - Write a description
 * - Write IT's
 * - A11y testing for the modal only
 * - Add mobile width once we finally start to store it in the AAG store
 * - Subtitle Link should "Link to the violations detail"
 * - Sort
 * - Next Button: Submit form
 * - AC for Apply submit comment
 * - Apply submit should display a success modal at the page level
 * - Localize the link in the subheading, can't without AC as it will need the message string
 * - second level of error alert: Required field (1) is missing.
 */
function SodMitigateModal({
  isOpen,
  setIsOpen,
}: SodMitigateModalProps): ReactElement {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [mitigateItems, setMitigateItems] =
    useState<MitigateItemType[]>(MOCK_MITIGATE_DATA);
  // Validation
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormCritical, setIsFormCritical] = useState(false);
  // Filter and sort
  const [isRecommendedOnly, setisRecommendedOnly] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const sectionRef = useRef(null);
  const l = useInvertedLocalize();

  const {
    modalIsland: {
      mitigateModalData: { rowId },
    },
  } = useTypedSelector((state) => state.aag.violationsTab);

  const { isScrolled: sectionRefIsScrolled }: { isScrolled: boolean } =
    useDetectScroll(null, sectionRef);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    } else {
      setTimeout(() => {
        setIsLoading(true);
      }, 200);
    }
  }, [isOpen]);

  // Reset All On Modal Close
  useEffect(() => {
    if (!isOpen) {
      setMitigateItems(MOCK_MITIGATE_DATA);
      setisRecommendedOnly(false);
      setSearchValue('');
      setIsFormCritical(false);
      setIsFormValid(false);
      setIsSubmitLoading(false);
    }
  }, [isOpen]);

  // Update form validity when selected items change
  useEffect(() => {
    // Filter only selected items
    const selectedItems = mitigateItems.filter((item) => item.isSelected);

    // Set form as valid only if:
    // - At least one item is selected.
    // - Every selected item has a start date, an end date, and a non-empty comment.
    const valid =
      selectedItems.length > 0 &&
      selectedItems.every(
        (item) =>
          item.selectedDates.startDate &&
          item.selectedDates.endDate &&
          item.comment.trim().length > 0
      );

    setIsFormValid(valid);
  }, [mitigateItems]);

  const handleCommentChange = (id: string, comment: string) => {
    setMitigateItems((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, comment } : item))
    );
  };

  const handleSort = () => {
    Logger.log('Sorting clicked');
  };

  const handleToggleSelect = (id: string) => {
    setMitigateItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, isSelected: !item.isSelected } : item
      )
    );
  };

  const handleDateSelection = (
    id: string,
    date: Date,
    dateType: DateTypeOptions
  ) => {
    setMitigateItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id
          ? {
              ...item,
              selectedDates: {
                ...item.selectedDates,
                [dateType]: date,
              },
            }
          : item
      )
    );
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const fakeMitigate = (): Promise<void> =>
    new Promise((resolve, reject) => {
      const delay = 1000 + Math.random() * 3000; // 1‑4s

      setTimeout(() => {
        Logger.log('Should have moved to redux ==>', rowId);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        Math.random() < 0.25 ? reject(new Error('1/4 fail chance')) : resolve();
      }, delay);
    });

  const handleSubmit = async () => {
    if (!isFormValid) {
      setIsFormCritical(true);

      return;
    }

    // 1. SET LOADING
    setIsSubmitLoading(true);

    try {
      // 2. CALL API WAIT FOR RESPONSE
      await fakeMitigate();

      // 3. (if success) DISPATCH THE ROW TO BE REMOVED:
      // - 1. SET THE REAL DATA IN REDUX
      // - 2. SET THE ROW TO BE REMOVED USING JUST THE ID

      setIsOpen(false);
    } catch (err) {
      Logger.error('Mitigation failed', err);
      setIsSubmitLoading(false);
    }
  };

  const switchLabel = (
    <Box className='SodMitigateModal-switchLabelWrapper'>
      <Icon kind='StarFilled' size='smallMedium' color={CF_NEU_BLD} />
      <Typography
        kind='body2'
        color={CF_NEU_BLD}
        className='SodMitigateModal-switchLabel'>
        {l('Recommended Only')}
      </Typography>
    </Box>
  );

  // Step 0: Loading Data
  const getModalBodyLoading = () => (
    <Box className='SodMitigateModal-bodyLoading'>
      <Loader
        kind='loop'
        format='main'
        color='brand'
        dataTestId={testIds.SOD_MITIGATE_MODAL_LOADER}
      />
    </Box>
  );

  // Step 1: Select Mitigation Date List
  const getModalBodyControls = () => (
    <Box className='SodMitigateModal-bodyControls'>
      {/* Left Side */}
      <Box className='SodMitigateModal-bodyControlsLeft'>
        <InputField
          name='basic'
          placeholder='Search'
          kind='search'
          size='small'
          prefixIcon={<Icon kind='Search' color={CF_NEU_STL} />}
          value={searchValue}
          setValue={setSearchValue}
          className='SodMitigateModal-searchInput'
        />
        <ButtonSelect
          kind='rectangle'
          label={l('Sort')}
          onClick={handleSort}
          isOpen={false}
          size='small'
          prefixIcon={<Icon kind='Sort' />}
          isVisualCueEnabled={false}
          isDisabled
        />
      </Box>
      {/* Right Side */}
      <Box className='SodMitigateModal-bodyControlsRight'>
        <Switch
          isChecked={isRecommendedOnly}
          onChange={() => setisRecommendedOnly(!isRecommendedOnly)}
          label={switchLabel}
          labelPosition='left'
          className='SodMitigateModal-switch'
        />
      </Box>
    </Box>
  );

  const getModalBodyList = () => {
    const matchesRecommendation = (item: MitigateItemType) =>
      !isRecommendedOnly || item.isRecommended || item.isSelected;

    const matchesSearch = (item: MitigateItemType) => {
      if (!searchValue) {
        return true;
      }

      const query = searchValue.toLowerCase();

      return (
        item.title.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.mitigateType.toLowerCase().includes(query)
      );
    };

    const filteredItems = mitigateItems
      .filter((item) => matchesRecommendation(item) && matchesSearch(item))
      .sort((a, b) => Number(b.isRecommended) - Number(a.isRecommended));

    if (filteredItems.length === 0) {
      return (
        <Box className='SodMitigateModal-emptyList'>
          <Typography kind='body1' color={CF_NEU_MED}>
            {l('No records match your search')}
          </Typography>
        </Box>
      );
    }

    return (
      <ul className='SodMitigateModal-list'>
        {filteredItems.map((item) => (
          <SodMitigateListItem
            key={item.id}
            item={item}
            onToggleSelect={handleToggleSelect}
            handleDateSelection={handleDateSelection}
            handleCommentChange={handleCommentChange}
            isFormCritical={isFormCritical}
          />
        ))}
      </ul>
    );
  };

  // Header Subtitle
  const getSubitle = () => (
    <Box className='SodMitigateModal-subtitleWithLink'>
      <Typography kind='body2' color={CF_NEU_MED}>
        {l('Apply one or more mitigating controls to violation')}
      </Typography>
      {/* eslint-disable-next-line no-warning-comments */}
      {/* TODO: Localizing this has had testing issues for now, will finish when link AC is completed */}
      <Link
        text='OracleCloud_GL_SOD_Enter Journal & Post Journal (by Joey Kookamunga)'
        color='neutral'
        target='_self'
        href='/'
        RouterComponent={RouterLink}
      />
    </Box>
  );

  // Footer
  const getModalFooter = () => (
    <Box className='SodMitigateModal-footerActionButtons'>
      <Button kind='subtle' size='medium' onClick={handleCancel}>
        {l('Cancel')}
      </Button>
      <Button
        kind={isFormValid ? 'filled' : 'outlined'}
        size='medium'
        isLoading={isSubmitLoading}
        onClick={handleSubmit}>
        {l('Apply')}
      </Button>
    </Box>
  );

  return (
    <ModalIsland
      onClose={() => setIsOpen(false)}
      isOpen={isOpen}
      title={l('Mitigate')}
      subtitle={getSubitle()}
      contentBody={
        <Box className='SodMitigateModal-body'>
          <AlertBanner
            colorTheme='Critical'
            title={l('Selection Required')}
            description={l('Please select at least one mitigating control.')}
            isColonVisible
            isVisible={isFormCritical}
            onCancel={() => {
              setIsFormCritical(false);
            }}
            isFloating
            position={{
              type: 'fixed',
              top: '7rem',
              right: '3rem',
              left: '3rem',
            }}
          />
          {isLoading ? (
            getModalBodyLoading()
          ) : (
            <>
              {getModalBodyControls()}
              {getModalBodyList()}
            </>
          )}
        </Box>
      }
      sectionRef={sectionRef}
      FooterActionBarComp={
        <FooterActionBar
          size='large'
          isScrolled={sectionRefIsScrolled}
          className='SodMitigateModal-footerActionBar'>
          {getModalFooter()}
        </FooterActionBar>
      }
      className='SodMitigateModal'
      sectionClassName='SodMitigateModal-section'
      bodyClassName='SodMitigateModal-bodyWrapper'
    />
  );
}

export default SodMitigateModal;
